import apiClient from "../helpers/apiClient";
import { format, isValid, parseISO } from "date-fns";

export const designationService = {
  getDesignations,
  getDesignation,
  createDesignation,
  updateDesignation,
  deleteDesignation,
  setDesignationStatus,
};

function displayValue(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

function formatDesignationDate(value) {
  if (value === null || value === undefined || value === "") return "-";

  const date =
    value instanceof Date
      ? value
      : typeof value === "string"
        ? parseISO(value)
        : new Date(value);

  return isValid(date) ? format(date, "dd MMM yyyy") : "-";
}

function findDesignationPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const wrapperKey = Object.keys(payload).find(
    (key) => key.toLowerCase() === "designation_info"
  );
  const wrapped = wrapperKey ? payload[wrapperKey] : undefined;

  if (Array.isArray(wrapped)) return wrapped;
  if (wrapped && typeof wrapped === "object") return [wrapped];
  if (payload.designation_id !== undefined) return [payload];
  return [];
}

function mapDesignation(raw = {}) {
  const createdAtRaw = raw.encoded_date;
  const lastChangedAtRaw = raw.last_changed_date;

  return {
    id: displayValue(raw.designation_id),
    name: displayValue(raw.designation_name),
    departmentId: displayValue(raw.department_id),
    departmentName: displayValue(raw.department_name),
    statusId: displayValue(raw.designation_status_id),
    statusName: displayValue(raw.designation_status_name),
    createdBy: displayValue(raw.encoded_by_name || raw.encoded_by_user_name),
    createdAtRaw,
    createdAt: formatDesignationDate(createdAtRaw),
    lastChangedBy: displayValue(
      raw.last_changed_by_name || raw.last_changed_by_user_name
    ),
    lastChangedAtRaw,
    lastChangedAt: formatDesignationDate(lastChangedAtRaw),
  };
}

function getErrorMessage(error) {
  return (
    error.response?.data?.msg ||
    error.response?.data?.message ||
    "Something went wrong"
  );
}

function extractWriteResult(response) {
  const data = response?.data;
  const messageObject = Array.isArray(data?.result_status)
    ? data.result_status[0]
    : Array.isArray(data)
      ? data[0]
      : data;
  const messageText = messageObject?.message_text || messageObject?.Message_Text;
  const refNo = messageObject?.message_ref_no ?? messageObject?.Message_Ref_No ?? null;

  return {
    message:
      messageText !== undefined && messageText !== null && messageText !== ""
        ? messageText
        : "Saved successfully.",
    refNo,
  };
}

async function handleRequest(request) {
  try {
    return await request();
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

async function handleWriteRequest(request) {
  try {
    const response = await request();
    return extractWriteResult(response);
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

function getDesignations() {
  return handleRequest(async () => {
    const response = await apiClient.get("/hr/designations");
    return findDesignationPayload(response?.data).map(mapDesignation);
  });
}

function getDesignation(id) {
  return handleRequest(async () => {
    const response = await apiClient.get(`/hr/designations/${id}`);
    const [designation] = findDesignationPayload(response?.data);
    return designation ? mapDesignation(designation) : null;
  });
}

function createDesignation(name, departmentId) {
  return handleWriteRequest(() =>
    apiClient.post("/hr/designations", {
      designation_name: name,
      department_id: departmentId,
    })
  );
}

function updateDesignation(id, name, statusId, departmentId) {
  return handleWriteRequest(() =>
    apiClient.put(`/hr/designations/${id}`, {
      designation_name: name,
      designation_status_id: statusId,
      department_id: departmentId,
    })
  );
}

function deleteDesignation(id) {
  return handleWriteRequest(() => apiClient.delete(`/hr/designations/${id}`));
}

function setDesignationStatus(id, statusId) {
  return handleWriteRequest(() =>
    apiClient.patch(`/hr/designations/${id}/status`, {
      designation_status_id: statusId,
    })
  );
}
